import { StanButton, StanUnsplashImageCropSelector, StanUnsplashImageCropSelectorRef } from '@/components';
import { useModal } from '@/hooks/useModal';
import { cn } from '@/lib';
import { HTMLAttributes, useRef, useState } from 'react';
import { Icon } from './Icon';
import { Loader } from './Loader';
import { UnsplashImageSelector } from './UnsplashImageSelector';

export interface Props extends HTMLAttributes<HTMLDivElement> {
    aspectRatio?: number;
    width?: number;
    image?: string;
    onDelete?: () => void;
    onUpdate?: (src: string) => void;
    onAdd?: (src: string) => void;
}

export const ImageUpload = ({
    image,
    aspectRatio,
    width = 500,
    className,
    onDelete,
    onUpdate,
    onAdd,
    ...rest
}: Props) => {
    const [loading, setLoading] = useState(false);
    const selectorImageRef = useRef<StanUnsplashImageCropSelectorRef>(null);
    const selectorModal = useModal();

    return (
        <div
            className={cn('bg-bg-muted relative grid max-h-full w-full overflow-hidden rounded-lg', className)}
            {...rest}
        >
            <div className='col-1 row-1 max-h-full w-full overflow-hidden'>
                {image ? (
                    <img
                        src={image}
                        alt='image'
                        className='animate-in fade-in fill-mode-backwards aspect-auto h-full min-h-full w-full min-w-full object-cover object-center'
                        onLoad={() => setLoading(false)}
                    />
                ) : (
                    <span className='bg-bg-muted block aspect-square' />
                )}
            </div>
            {loading && (
                <div className='absolute inset-0 flex items-center justify-center'>
                    <Loader />
                </div>
            )}
            {!loading && (
                <div
                    className={cn(
                        'col-1 row-1 flex flex-nowrap items-start justify-end gap-1 p-2',
                        !image && 'items-center justify-center'
                    )}
                >
                    {!image ? (
                        <StanButton
                            name='add-new-image'
                            size='xs'
                            variant='outline'
                            onClick={() => {
                                selectorImageRef.current?.open();
                            }}
                        >
                            Add Image
                        </StanButton>
                    ) : (
                        <StanButton
                            name='upload-image'
                            size='sm'
                            color='secondary'
                            className='aspect-square !min-w-fit'
                            onClick={() => {
                                selectorImageRef.current?.open();
                            }}
                        >
                            <Icon name='edit' />
                        </StanButton>
                    )}
                    {image && onDelete && (
                        <StanButton
                            name='delete-image'
                            onClick={onDelete}
                            size='sm'
                            color='secondary'
                            className='aspect-square !min-w-fit'
                        >
                            <Icon name='delete' className='text-negative-moderate text-lg' />
                        </StanButton>
                    )}
                </div>
            )}

            {aspectRatio ? (
                <StanUnsplashImageCropSelector
                    onUploadError={() => setLoading(false)}
                    aspectRatio={aspectRatio}
                    ref={selectorImageRef}
                    onClose={() => selectorModal.close()}
                    onCropped={(croppedDataUrl: string, croppedFileName: string, croppedFileUrl: string) => {
                        setLoading(true);
                        if (image) onUpdate?.(croppedFileUrl);
                        else onAdd?.(croppedFileUrl);
                    }}
                />
            ) : (
                <UnsplashImageSelector
                    onStartUpload={() => setLoading(true)}
                    onUploadError={() => setLoading(false)}
                    width={width}
                    ref={selectorImageRef}
                    onChange={(src: string) => {
                        if (image) onUpdate?.(src);
                        else onAdd?.(src);
                    }}
                />
            )}
        </div>
    );
};
